import { Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { createStore, select, withProps } from '@ngneat/elf';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { Storage } from '@core/utils/storage';
import { GlobalFilter } from '@core/models/global-filter';

interface GlobalFilterProp {
  visible: boolean;
  selectSingleHotel: boolean;
  filter: GlobalFilter | null;
  // True while a single-hotel details page (e.g. Channel Details) is open
  inDetailsScope: boolean;
  // The filter the user had before entering the details page, restored on exit
  detailsSnapshot: GlobalFilter | null;
}
export interface SelectedHotelInfo {
  id: string;
  name: string;
  code: string;
  displayName?: string;
}

const EMPTY_FILTER: GlobalFilter = {
  enterprises: [],
  ownershipGroups: [],
  brands: [],
  regions: [],
  hotels: [],
};

const globalFilterStore = createStore(
  { name: 'globalFilter' },
  withProps<GlobalFilterProp>({
    visible: false,
    selectSingleHotel: false,
    filter: null,
    inDetailsScope: false,
    detailsSnapshot: null,
  })
);

persistState(globalFilterStore, {
  key: 'globalFilter',
  storage: localStorageStrategy,
  // While on a details page, persist the user's original selection instead of the
  // temporary single-hotel filter, so a refresh or closed tab never keeps it permanently
  preStorageUpdate: (_, state) => {
    const { inDetailsScope, detailsSnapshot, ...rest } = state;
    return inDetailsScope ? { ...rest, filter: detailsSnapshot } : rest;
  },
});

@Injectable({ providedIn: 'root' })
export class GlobalFilterRepository {
  visible$: Observable<boolean> = globalFilterStore.pipe(select((state) => state.visible));
  private _selectedHotelInfo = signal<SelectedHotelInfo | null>(null);

  // ========== CHECKPOINT 4 & 5 MONITOR ==========
  constructor() {
    // Monitor all store changes and automatic persistence
    globalFilterStore.subscribe((state) => {
      // console.log('🟡 CHECKPOINT 4 & 5: Store Changed + Auto-Persisted', {
      //   state,
      //   localStorageValue: JSON.parse(localStorage.getItem('globalFilter') || '{}'),
      //   note: 'persistState plugin already wrote to localStorage synchronously',
      // });
    });
  }
  // ==============================================

  get selectedHotelInfo() {
    return this._selectedHotelInfo.asReadonly();
  }

  setSelectedHotelInfo(hotelInfo: SelectedHotelInfo | null): void {
    this._selectedHotelInfo.set(hotelInfo);

    // single hotel info Save to localStorage
    if (hotelInfo) {
      Storage.setItem('selectedHotelInfo', hotelInfo);
    } else {
      Storage.removeItem('selectedHotelInfo');
    }
  }

  restoreSelectedHotelInfo(): void {
    const storedHotelInfo = Storage.getItem<SelectedHotelInfo>('selectedHotelInfo');
    if (storedHotelInfo) {
      this._selectedHotelInfo.set(storedHotelInfo);
    }
  }

  selectSingleHotel$: Observable<boolean> = globalFilterStore.pipe(
    select((state) => state.selectSingleHotel)
  );

  filter$: Observable<GlobalFilter | null> = globalFilterStore.pipe(
    select((state) => state.filter)
  );

  setVisible = (visible: boolean) => {
    globalFilterStore.update((state) => ({ ...state, visible }));
  };

  setSelectSingleHotel = (limit: boolean) => {
    globalFilterStore.update((state) => ({ ...state, selectSingleHotel: limit }));
  };

  setFilter = (filter: GlobalFilter) => {
    // ========== CHECKPOINT 3 START ==========
    // console.log('🟢 CHECKPOINT 3: Repository Received', {
    //   receivedFilter: filter,
    //   currentState: globalFilterStore.getValue(),
    // });
    // ========================================
    globalFilterStore.update((state) => ({ ...state, filter }));

    // ========== CHECKPOINT 3 END ==========
    // console.log('🟢 CHECKPOINT 3: Store Update Called', {
    //   newState: globalFilterStore.getValue(),
    //   note: 'persistState subscription will fire now',
    // });
    // ======================================
  };

  /**
   * Narrow the global filter to the hotel opened on a details page.
   * Only the first call snapshots the current selection, so a refresh or switching
   * hotels from the top filter never overwrites the user's original selection.
   * Must be called before setSelectSingleHotel(true).
   */
  enterDetailsScope = (hotel: SelectedHotelInfo) => {
    globalFilterStore.update((state) => {
      const current = state.filter ?? EMPTY_FILTER;
      const isSameHotel = current.hotels.length === 1 && current.hotels[0] === hotel.id;
      return {
        ...state,
        inDetailsScope: true,
        detailsSnapshot: state.inDetailsScope ? state.detailsSnapshot : current,
        filter: isSameHotel ? current : { ...current, hotels: [hotel.id] },
      };
    });
    this.setSelectedHotelInfo(hotel);
  };

  /** Restore the selection the user had before entering the details page. */
  exitDetailsScope = () => {
    const { inDetailsScope, detailsSnapshot } = globalFilterStore.getValue();
    if (!inDetailsScope) return;

    globalFilterStore.update((state) => ({
      ...state,
      inDetailsScope: false,
      detailsSnapshot: null,
      filter: detailsSnapshot ?? EMPTY_FILTER,
    }));
    this.setSelectedHotelInfo(null);
  };
}
